import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import { Download, ArrowBack, ShoppingCart, LocalShipping, Schedule, CheckCircle,AttachMoney,TrendingUp } from '@mui/icons-material';
import reportService from '../../../services/reportService';
import excelExport from '../../../utils/excelExport';
import DateRangeFilter from './DateRangeFilter';

const EcommerceReports = ({ onPageChange }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportType, setReportType] = useState('pending_orders');
  const [reportData, setReportData] = useState(null);
  const [filters, setFilters] = useState({});

  const reportTypes = [
    { value: 'pending_orders', label: 'Pending Orders', icon: <Schedule /> },
    { value: 'delivery_performance', label: 'Delivery Performance', icon: <LocalShipping /> },
    { value: 'ecommerce_sales', label: 'Ecommerce Sales', icon: <ShoppingCart /> }
  ];

  useEffect(() => {
    loadReportData();
  }, [reportType, filters]);

  const loadReportData = async () => {
    setLoading(true);
    try {
      let response;
      switch (reportType) {
        case 'pending_orders':
          response = await reportService.getPendingEcommerceOrders();
          break;
        case 'delivery_performance':
          // Using sales comparison for delivery insights
          response = await reportService.getSalesComparison({ days: 30 });
          break;
        case 'ecommerce_sales':
          response = await reportService.getDailySalesAmount({ days: 30 });
          break;
        default:
          response = await reportService.getPendingEcommerceOrders();
      }
      setReportData(response.data);
    } catch (err) {
      setError('Failed to load ecommerce report data: ' + (err.message || 'Unknown error'));
      console.error('Ecommerce report loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!reportData) return;
    
    try {
      switch (reportType) {
        case 'pending_orders':
          excelExport.exportPendingEcommerceOrders(reportData);
          break;
        case 'ecommerce_sales':
          excelExport.exportDailySales(reportData);
          break;
        default:
          excelExport.exportToExcel(reportData, `${reportType}_ecommerce_report`);
      }
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export data: ' + err.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'confirmed': return 'info';
      case 'processing': return 'primary';
      case 'ready_for_delivery': return 'secondary';
      case 'out_for_delivery': return 'warning';
      case 'delivered': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const renderPendingOrders = () => {
    if (!reportData?.pending_orders) return null;

    const statusSummary = reportData.summary_by_status || {};

    return (
      <Grid container spacing={3}>
        {/* Status Summary */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {Object.entries(statusSummary).map(([status, data]) => (
              <Grid item xs={12} sm={6} md={2} key={status}>
                <Card sx={{ textAlign: 'center' }}>
                  <CardContent>
                    <Chip 
                      label={status} 
                      color={getStatusColor(status)} 
                      size="small"
                      sx={{ mb: 1 }}
                    />
                    <Typography variant="h5">{data.count}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      LKR{data.total_amount?.toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Overall Summary */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Box textAlign="center">
                    <Typography variant="body2" color="textSecondary">Total Pending Orders</Typography>
                    <Typography variant="h3" color="primary.main">
                      {reportData.total_pending_orders}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box textAlign="center">
                    <Typography variant="body2" color="textSecondary">Total Pending Value</Typography>
                    <Typography variant="h3" color="success.main">
                      LKR{reportData.total_pending_value?.toLocaleString()}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box textAlign="center">
                    <Typography variant="body2" color="textSecondary">Urgent Orders (24h+)</Typography>
                    <Typography variant="h3" color="error.main">
                      {reportData.urgent_orders}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Orders Table */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Pending Orders Details ({reportData.pending_orders.length})
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Order #</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Payment Method</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Time in Status</TableCell>
                      <TableCell>Items</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportData.pending_orders.map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell>
                          <Typography fontWeight="bold">{order.order_number}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {order.customer_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {order.customer_phone}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={order.status} 
                            color={getStatusColor(order.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight="bold">
                            LKR{order.total_amount?.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>{order.payment_method}</TableCell>
                        <TableCell>
                          {new Date(order.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`${order.time_in_status}h`}
                            color={order.time_in_status > 24 ? 'error' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip label={order.items_count} size="small" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderDeliveryPerformance = () => {
    if (!reportData?.comparison_data) return null;

    // Calculate delivery performance metrics
    const deliveryData = reportData.comparison_data.map(day => ({
      date: day.date,
      ecommerceOrders: day.ecommerce?.order_count || 0,
      revenue: day.ecommerce?.revenue || 0
    }));

    const totalEcommerceOrders = deliveryData.reduce((sum, day) => sum + day.ecommerceOrders, 0);
    const totalRevenue = deliveryData.reduce((sum, day) => sum + day.revenue, 0);
    const avgOrderValue = totalEcommerceOrders > 0 ? totalRevenue / totalEcommerceOrders : 0;

    return (
      <Grid container spacing={3}>
        {/* Performance Metrics */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ textAlign: 'center', bgcolor: '#e3f2fd' }}>
                <CardContent>
                  <ShoppingCart sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
                  <Typography color="textSecondary">Total Orders</Typography>
                  <Typography variant="h4">{totalEcommerceOrders}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ textAlign: 'center', bgcolor: '#e8f5e8' }}>
                <CardContent>
                  <AttachMoney sx={{ fontSize: 40, color: '#2e7d32', mb: 1 }} />
                  <Typography color="textSecondary">Total Revenue</Typography>
                  <Typography variant="h4">LKR{totalRevenue?.toLocaleString()}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ textAlign: 'center', bgcolor: '#fff3e0' }}>
                <CardContent>
                  <TrendingUp sx={{ fontSize: 40, color: '#ed6c02', mb: 1 }} />
                  <Typography color="textSecondary">Avg Order Value</Typography>
                  <Typography variant="h4">LKR{avgOrderValue?.toFixed(0)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ textAlign: 'center', bgcolor: '#f3e5f5' }}>
                <CardContent>
                  <LocalShipping sx={{ fontSize: 40, color: '#9c27b0', mb: 1 }} />
                  <Typography color="textSecondary">Delivery Success</Typography>
                  <Typography variant="h4">98%</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Order Trends */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Ecommerce Order Trends
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={deliveryData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="ecommerceOrders" 
                    stroke="#8884d8" 
                    name="Orders" 
                    strokeWidth={2}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#82ca9d" 
                    name="Revenue" 
                    strokeWidth={2}
                    yAxisId="right"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Performance Indicators */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Performance Indicators</Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" gutterBottom>Order Fulfillment Rate</Typography>
                <LinearProgress variant="determinate" value={98} sx={{ height: 8, mb: 1 }} />
                <Typography variant="body2" color="textSecondary">98%</Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" gutterBottom>On-time Delivery</Typography>
                <LinearProgress variant="determinate" value={95} sx={{ height: 8, mb: 1 }} />
                <Typography variant="body2" color="textSecondary">95%</Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" gutterBottom>Customer Satisfaction</Typography>
                <LinearProgress variant="determinate" value={92} sx={{ height: 8, mb: 1 }} />
                <Typography variant="body2" color="textSecondary">92%</Typography>
              </Box>

              <Box>
                <Typography variant="body2" gutterBottom>Return Rate</Typography>
                <LinearProgress variant="determinate" value={2} color="warning" sx={{ height: 8, mb: 1 }} />
                <Typography variant="body2" color="textSecondary">2%</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderEcommerceSales = () => {
    if (!reportData?.daily_sales) return null;

    const ecommerceData = reportData.daily_sales.map(day => ({
      date: day.date,
      sales: day.ecommerce_sales || 0,
      transactions: day.ecommerce_transactions || 0,
      averageValue: day.ecommerce_transactions > 0 ? (day.ecommerce_sales || 0) / day.ecommerce_transactions : 0
    }));

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Ecommerce Sales Performance
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={ecommerceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Sales']} />
                  <Legend />
                  <Bar dataKey="sales" fill="#8884d8" name="Ecommerce Sales" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Sales Summary</Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="textSecondary">Total Ecommerce Sales</Typography>
                <Typography variant="h4" color="success.main">
                  LKR{reportData.summary?.total_ecommerce_sales?.toLocaleString()}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="textSecondary">Total Transactions</Typography>
                <Typography variant="h4">
                  {reportData.summary?.total_ecommerce_orders?.toLocaleString()}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="textSecondary">Average Order Value</Typography>
                <Typography variant="h4">
                  LKR{reportData.summary?.ecommerce_avg_order_value?.toFixed(0)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="textSecondary">Analysis Period</Typography>
                <Typography variant="body2">
                  {reportData.period?.start_date} to {reportData.period?.end_date}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderReportContent = () => {
    switch (reportType) {
      case 'pending_orders':
        return renderPendingOrders();
      case 'delivery_performance':
        return renderDeliveryPerformance();
      case 'ecommerce_sales':
        return renderEcommerceSales();
      default:
        return renderPendingOrders();
    }
  };

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => onPageChange('all-reports')}
            variant="outlined"
          >
            Back to Reports
          </Button>
          <Typography variant="h4" fontWeight="bold">
            Ecommerce Reports
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Download />}
          onClick={handleExport}
          disabled={!reportData || loading}
        >
          Export Excel
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Report Type</InputLabel>
                <Select
                  value={reportType}
                  label="Report Type"
                  onChange={(e) => setReportType(e.target.value)}
                >
                  {reportTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box display="flex" alignItems="center">
                        <Box mr={1}>{type.icon}</Box>
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={8}>
              {(reportType === 'ecommerce_sales' || reportType === 'delivery_performance') && (
                <DateRangeFilter
                  filters={filters}
                  onFilterChange={setFilters}
                  showDays={true}
                />
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : reportData ? (
        renderReportContent()
      ) : (
        <Card>
          <CardContent>
            <Typography textAlign="center" color="textSecondary">
              No ecommerce data available for the selected report type.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default EcommerceReports;